import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getArtifact,
  buyArtifact,
  type Detail,
  type BuyError,
  type BuyResponse,
} from "@/lib/api/marketplace";
import { useAuth } from "@/lib/auth";

export const Route = createFileRoute("/marketplace/$id")({
  component: ArtifactDetailPage,
});

function ArtifactDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ["artifact", id],
    queryFn: () => getArtifact(id),
  });

  const [buyError, setBuyError] = useState<string | null>(null);

  const buyMut = useMutation<BuyResponse, BuyError, void>({
    mutationFn: () => buyArtifact(id),
    onSuccess: () => {
      qc.setQueryData<Detail | undefined>(["artifact", id], (old) =>
        old ? { ...old, is_owned: true } : old,
      );
      void navigate({ to: "/library" });
    },
    onError: (err) => {
      switch (err.kind) {
        case "unauthorized":
          void navigate({ to: "/login", search: { next: `/marketplace/${id}` } });
          return;
        case "insufficient_funds":
          setBuyError("Your wallet doesn't have enough coins for this artifact.");
          return;
        case "self_purchase":
          setBuyError("You can't buy your own automaton's artifact.");
          return;
        case "already_owned":
          void qc.invalidateQueries({ queryKey: ["artifact", id] });
          return;
        case "not_for_sale":
          setBuyError("This artifact is no longer for sale.");
          return;
        case "not_found":
          setBuyError("Artifact not found.");
          return;
        default:
          setBuyError("Something went wrong. Try again.");
      }
    },
  });

  if (detail.isPending) return <main aria-busy>Loading…</main>;
  if (detail.isError) return <main role="alert">Couldn't load this artifact.</main>;

  const a = detail.data;

  return (
    <main className="mkt-detail">
      <header className="mkt-detail-header">
        <h1 className="mkt-detail-title">{a.title}</h1>
        <p className="mkt-detail-summary">{a.summary}</p>
        {a.automaton_status === "dead" && (
          <span className="mkt-badge-stopped" aria-label="from a stopped automaton">
            from a stopped automaton
          </span>
        )}
      </header>

      <section className="mkt-detail-description">
        <p>{a.description}</p>
      </section>

      <aside className="mkt-detail-panel">
        <div className="mkt-detail-price">{a.price_c}c</div>
        <div className="mkt-detail-seller">by {a.automaton_name}</div>

        {a.is_owned ? (
          <a className="mkt-buy-btn" href="/library" data-testid="inventory-link">
            Show in inventory
          </a>
        ) : (
          <button
            className="mkt-buy-btn"
            disabled={buyMut.isPending || !user}
            onClick={() => {
              setBuyError(null);
              buyMut.mutate();
            }}
          >
            {buyMut.isPending ? "Buying…" : `Buy for ${a.price_c}c`}
          </button>
        )}

        {!user && !a.is_owned && (
          <p className="mkt-detail-hint">
            <a href={`/login?next=/marketplace/${id}`}>Log in</a> to buy.
          </p>
        )}

        {buyError && (
          <p role="alert" className="mkt-detail-error">
            {buyError}
          </p>
        )}
      </aside>
    </main>
  );
}
